import Konva from 'konva';
import { MutableRefObject, useEffect } from 'react';

interface UseStageTouchGesturesParams {
  stageRef: MutableRefObject<Konva.Stage | null>;
  scaleRef: MutableRefObject<number>;
  positionRef: MutableRefObject<{ x: number; y: number }>;
  minScale: number;
  maxScale: number;
  setScale: (scale: number) => void;
  setPosition: (pos: { x: number; y: number }) => void;
  /** Cancel any in-flight pan animation owned by the parent (e.g. centering tween). */
  cancelExternalAnimation?: () => void;
  enabled?: boolean;
  /**
   * Bumped by the parent whenever the Stage instance is (re)created. Since `stageRef`
   * is a mutable ref it doesn't trigger effect re-runs on its own, so callers that mount
   * the Stage lazily (inside a Drawer, behind a feature flag, etc.) must surface a
   * trigger here so this hook re-attaches event handlers to the new Stage.
   */
  rebindTrigger?: unknown;
}

interface TouchPointer {
  x: number;
  y: number;
}

const TAP_MOVE_THRESHOLD_PX = 12;

const getTouchPointer = (touch: Touch, stage: Konva.Stage): TouchPointer => {
  const rect = stage.container().getBoundingClientRect();

  return {
    x: touch.clientX - rect.left,
    y: touch.clientY - rect.top,
  };
};

const distance = (a: TouchPointer, b: TouchPointer): number => Math.hypot(a.x - b.x, a.y - b.y);

const midpoint = (a: TouchPointer, b: TouchPointer): TouchPointer => ({
  x: (a.x + b.x) / 2,
  y: (a.y + b.y) / 2,
});

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const findBookingShapeNode = (node: Konva.Node | null): Konva.Node | null => {
  let current: Konva.Node | null = node;

  while (current) {
    if (current.name() === 'shape' && current.id()) {
      return current;
    }

    current = current.parent;
  }

  return null;
};

const fireShapeTap = (stage: Konva.Stage, pointer: TouchPointer, sourceEvent: TouchEvent): void => {
  const hitNode = stage.getIntersection(pointer);

  if (!hitNode) {
    return;
  }

  const shapeNode = findBookingShapeNode(hitNode);

  if (!shapeNode) {
    return;
  }

  shapeNode.fire(
    'tap',
    {
      evt: sourceEvent,
      target: shapeNode,
      currentTarget: shapeNode,
      type: 'tap',
    },
    true,
  );
};

/**
 * Wires pinch-to-zoom + two-finger pan onto a Konva Stage. Single-finger drag remains
 * handled by the Stage's own `draggable` behavior; this hook disables that drag while
 * two fingers are down to avoid the gestures fighting.
 *
 * On touch devices, Stage drag often swallows `onTap` on child shapes. We detect a
 * stationary single-finger release and synthesize a tap via hit-testing.
 */
export const useStageTouchGestures = (params: UseStageTouchGesturesParams): void => {
  const {
    stageRef,
    scaleRef,
    positionRef,
    minScale,
    maxScale,
    setScale,
    setPosition,
    cancelExternalAnimation,
    enabled = true,
    rebindTrigger,
  } = params;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const stage = stageRef.current;

    if (!stage) {
      return undefined;
    }

    const el = stage.container();

    el.style.touchAction = 'none';

    let gestureStartDistance = 0;
    let gestureStartScale = scaleRef.current;
    let gestureStartMid: TouchPointer = { x: 0, y: 0 };
    let gestureStartStagePos = { ...positionRef.current };
    /** World-coords point under the initial midpoint (so we can keep it stationary while scaling). */
    let pinchAnchorWorld: TouchPointer = { x: 0, y: 0 };
    let isPinching = false;
    let stageWasDraggable = stage.draggable();
    let singleTouchStart: TouchPointer | null = null;
    let singleTouchMoved = false;
    let singleTouchStagePos = { x: 0, y: 0 };

    const handleTouchStart = (e: TouchEvent): void => {
      const touches = e.touches;

      if (touches.length === 1) {
        singleTouchStart = getTouchPointer(touches[0], stage);
        singleTouchMoved = false;
        singleTouchStagePos = { x: stage.x(), y: stage.y() };

        return;
      }

      singleTouchStart = null;
      singleTouchMoved = false;

      if (touches.length !== 2) {
        return;
      }

      cancelExternalAnimation?.();

      const p1 = getTouchPointer(touches[0], stage);
      const p2 = getTouchPointer(touches[1], stage);

      gestureStartDistance = distance(p1, p2);
      gestureStartScale = scaleRef.current;
      gestureStartMid = midpoint(p1, p2);
      gestureStartStagePos = { ...positionRef.current };
      pinchAnchorWorld = {
        x: (gestureStartMid.x - gestureStartStagePos.x) / gestureStartScale,
        y: (gestureStartMid.y - gestureStartStagePos.y) / gestureStartScale,
      };

      stageWasDraggable = stage.draggable();
      stage.draggable(false);
      isPinching = true;

      e.preventDefault();
    };

    const handleTouchMove = (e: TouchEvent): void => {
      const touches = e.touches;

      if (touches.length === 1 && singleTouchStart && !isPinching) {
        const pointer = getTouchPointer(touches[0], stage);

        if (distance(pointer, singleTouchStart) > TAP_MOVE_THRESHOLD_PX) {
          singleTouchMoved = true;
        }

        return;
      }

      if (!isPinching) {
        return;
      }

      if (touches.length !== 2 || gestureStartDistance === 0) {
        return;
      }

      const p1 = getTouchPointer(touches[0], stage);
      const p2 = getTouchPointer(touches[1], stage);

      const currentDistance = distance(p1, p2);
      const currentMid = midpoint(p1, p2);

      const rawScale = gestureStartScale * (currentDistance / gestureStartDistance);
      const nextScale = clamp(rawScale, minScale, maxScale);

      const nextPos = {
        x: currentMid.x - pinchAnchorWorld.x * nextScale,
        y: currentMid.y - pinchAnchorWorld.y * nextScale,
      };

      scaleRef.current = nextScale;
      positionRef.current = nextPos;
      setScale(nextScale);
      setPosition(nextPos);

      e.preventDefault();
    };

    const handleTouchEnd = (e: TouchEvent): void => {
      if (!isPinching && singleTouchStart && !singleTouchMoved && e.changedTouches.length > 0) {
        const stageDragged = Math.hypot(stage.x() - singleTouchStagePos.x, stage.y() - singleTouchStagePos.y) > TAP_MOVE_THRESHOLD_PX;

        if (!stageDragged) {
          const pointer = getTouchPointer(e.changedTouches[0], stage);

          fireShapeTap(stage, pointer, e);
        }
      }

      singleTouchStart = null;
      singleTouchMoved = false;

      if (!isPinching) {
        return;
      }

      if (e.touches.length < 2) {
        isPinching = false;
        stage.draggable(stageWasDraggable);
      }
    };

    const nonPassiveTouch: AddEventListenerOptions = { passive: false };

    el.addEventListener('touchstart', handleTouchStart, nonPassiveTouch);
    el.addEventListener('touchmove', handleTouchMove, nonPassiveTouch);
    el.addEventListener('touchend', handleTouchEnd, nonPassiveTouch);
    el.addEventListener('touchcancel', handleTouchEnd, nonPassiveTouch);

    return (): void => {
      el.style.touchAction = '';
      el.removeEventListener('touchstart', handleTouchStart, nonPassiveTouch);
      el.removeEventListener('touchmove', handleTouchMove, nonPassiveTouch);
      el.removeEventListener('touchend', handleTouchEnd, nonPassiveTouch);
      el.removeEventListener('touchcancel', handleTouchEnd, nonPassiveTouch);
    };
    // Mutable refs (stageRef, scaleRef, positionRef) are read inside handlers; Stage
    // lifecycle is surfaced via `rebindTrigger`. Omitting stageRef avoids useless reruns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, minScale, maxScale, setScale, setPosition, cancelExternalAnimation, rebindTrigger]);
};
